import { put, select, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import basicFlow, { genericErrorHandler } from './asyncHandler';
import { routeWatcher } from './rotas.saga';
import { authenticatedRequest, unauthenticatedRequest } from '../utils/api';
import {
  oportunidadesSuccess,
  oportunidadesRequest,
  oportunidadesFailure,
  oportunidadeSuccess,
  oportunidadeFailure,
  oportunidadeRequest,
  oportunidadeImovelIqSuccess,
  oportunidadeImovelIqFailure,
  oportunidadeImovelIqRequest,
  formatarOportunidade,
  buscarCepRequest,
  buscarCepSuccess,
  buscarCepFailure,
  editarOportunidadeRequest,
  editarOportunidadeSuccess,
  editarOportunidadeFailure,
  editarDataPrevistaRequest,
  editarDataPrevistaFailure,
  editarDataPrevistaSuccess,
  editarTemperaturaRequest,
  editarTemperaturaFailure,
  editarTemperaturaSuccess,
  editarSituacaoRequest,
  editarSituacaoFailure,
  editarSituacaoSuccess,
  obterEtapasRequest,
  obterEtapasFailure,
  obterEtapasSuccess,
  editarOportunidadeImovelIqRequest,
  editarOportunidadeImovelIqFailure,
  editarOportunidadeImovelIqSuccess,
  alterarMenuSelecionado,
  obterFunilDeVendasRequest,
  obterFunilDeVendasSuccess,
  obterFunilDeVendasFailure,
  salvarFiltrosGrid,
  setPaginationModel,
  setSortModel
} from '../reducers/oportunidade.reducer';
import {
  regionalRequest,
  parceiroRequest,
  usuariosParceirosRequest,
  usuariosRequest,
  operacaoRequest,
  etapasRequest,
  bancosRequest
} from '../reducers/dominios.reducer';
import { getPayload } from '../selectors/routes.selectors';
import { dominioSelectors } from 'selectors/index';
import { removeNonDigitsFromString } from 'utils/basic';
import { types as routes } from '../reducers/rotas.actions';
import formatters from '../utils/formatters';
import * as actions from '../reducers/compradores.reducer';
import * as vendedoresActions from '../reducers/vendedores.reducer';
import * as atividadesActions from '../reducers/atividades.reducer';
import * as fupActions from '../reducers/fup.reducer';
import * as documentosActions from '../reducers/documentos.reducer';

const buscarCep = basicFlow({
  actionGenerator: buscarCepRequest,
  actionFailure: buscarCepFailure,
  actionSuccess: buscarCepSuccess,
  api: ({ value }) => {
    return unauthenticatedRequest({
      url: `https://viacep.com.br/ws/${value}/json/`,
      method: 'get',
      isCep: true
    });
  },
  postSuccess: function* ({ response, original }) {
    if (!!original.successCallback) {
      yield original.successCallback(response, original.value);
    }
  },
  postFailure: function* (props) {
    if (!!props.original.failureCallback) {
      yield props.original.failureCallback(props);
    }
  }
});

// ----------------------------------------------------------------------------
// Tradutor: filtros do MUI X (que armazenam nomes/labels) -> filtros do
// /oportunidade/search (que recebem ids/codigos). Os mapas de dominio sao
// lidos da store para resolver nome -> id.
// ----------------------------------------------------------------------------
const TEMPERATURA_LABEL_TO_CODE = {
  Quente: 'Q',
  Morna: 'M',
  Fria: 'F'
};
const SITUACAO_LABEL_TO_CODE = {
  Ativa: 'A',
  Cancelada: 'C',
  'Contrato Emitido': 'E'
};

const lookupIds = (values, list, nameKey, idKey) => {
  if (!values || !values.length || !list?.length) return [];
  const set = new Set(values);
  return list
    .filter((item) => set.has(item[nameKey]))
    .map((item) => item[idKey])
    .filter((v) => v !== undefined && v !== null);
};

const dateOperatorToRange = (operator, value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const iso = date.toISOString().slice(0, 10);
  switch (operator) {
    case 'is':
      return { from: iso, to: iso };
    case 'after':
      return { from: iso, fromExclusive: true };
    case 'onOrAfter':
      return { from: iso };
    case 'before':
      return { to: iso, toExclusive: true };
    case 'onOrBefore':
      return { to: iso };
    default:
      return null;
  }
};

const buildFiltersFromGrid = (state) => {
  const items = state.oportunidade.filtrosGrid?.items ?? [];
  const dominios = state.dominios ?? {};
  const filters = {};

  for (const item of items) {
    const { field, value, operator } = item;
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      continue;
    }
    const arr = Array.isArray(value) ? value : [value];

    switch (field) {
      case 'parceiro':
        filters.idParceiro = lookupIds(
          arr,
          dominios.parceiro?.data,
          'nomeParceiro',
          'idParceiro'
        );
        break;
      case 'tipoOperacao':
        filters.idOperacao = lookupIds(
          arr,
          dominios.operacao?.data,
          'nomeOperacao',
          'idOperacao'
        );
        break;
      case 'regional':
        filters.idRegional = lookupIds(
          arr,
          dominios.regional?.data,
          'nomeRegional',
          'idRegional'
        );
        break;
      case 'usuarioParceiro':
        filters.idUsuarioParceiro = lookupIds(
          arr,
          dominios.usuarioParceiro?.data,
          'nomeUsuarioParceiro',
          'idUsuarioParceiro'
        );
        break;
      case 'consultor':
        filters.idUsuarioConsultor = arr
          .map((v) => Number(v))
          .filter((v) => Number.isInteger(v));
        break;
      case 'analista':
        filters.idUsuarioAnalista = arr
          .map((v) => Number(v))
          .filter((v) => Number.isInteger(v));
        break;
      case 'banco':
        filters.idBancoEscolhido = lookupIds(
          arr,
          dominios.listarBancos?.data,
          'nomeBanco',
          'idBanco'
        );
        break;
      case 'tipoTemperatura':
        filters.tipoTemperatura = arr
          .map((v) => TEMPERATURA_LABEL_TO_CODE[v])
          .filter(Boolean);
        break;
      case 'tipoSituacao':
        filters.tipoSituacao = arr
          .map((v) => SITUACAO_LABEL_TO_CODE[v])
          .filter(Boolean);
        break;
      case 'etapa':
        filters.idEtapa = lookupIds(
          arr,
          dominios.etapa?.data,
          'nomeEtapa',
          'idEtapa'
        );
        break;
      case 'dataOportunidade': {
        const range = dateOperatorToRange(operator, value);
        if (range?.from) filters.dataOportunidadeFrom = range.from;
        if (range?.to) filters.dataOportunidadeTo = range.to;
        break;
      }
      case 'dataPrevistaEmissaoContrato': {
        const range = dateOperatorToRange(operator, value);
        if (range?.from) filters.dataPrevistaEmissaoFrom = range.from;
        if (range?.to) filters.dataPrevistaEmissaoTo = range.to;
        break;
      }
      case 'codigoOportunidade':
      case 'comprador':
        filters.busca = String(arr[0] ?? '');
        break;
      default:
        break;
    }
  }

  return filters;
};

const buildSearchBody = (state) => {
  const pagination = state.oportunidade.paginationModel ?? {
    page: 0,
    pageSize: 10
  };
  const sortItem = state.oportunidade.sortModel?.[0];
  return {
    page: (pagination.page ?? 0) + 1, // MUI X e zero-indexed; backend e 1-indexed
    pageSize: pagination.pageSize ?? 50,
    sort: sortItem
      ? { field: sortItem.field, direction: sortItem.sort || 'desc' }
      : undefined,
    filters: buildFiltersFromGrid(state)
  };
};

const obterFunilApi = (body) => {
  return authenticatedRequest({
    url: '/oportunidade/funil/search',
    method: 'POST',
    body
  });
};

const obterFunil = basicFlow({
  actionGenerator: obterFunilDeVendasRequest,
  actionFailure: obterFunilDeVendasFailure,
  actionSuccess: obterFunilDeVendasSuccess,
  api: obterFunilApi,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const listarOportunidadesApi = (body) => {
  return authenticatedRequest({
    url: '/oportunidade/search',
    method: 'POST',
    body
  });
};

const listarOportunidades = basicFlow({
  actionGenerator: oportunidadesRequest,
  actionFailure: oportunidadesFailure,
  actionSuccess: oportunidadesSuccess,
  api: listarOportunidadesApi,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

// Dispara a busca paginada + funil usando o estado atual da grid (filtros,
// pagina, ordenacao). Reutilizado pelo route watcher e pelo watcher de
// mudancas de filtro/pagina/ordenacao.
function* dispararBuscaOportunidades() {
  const state = yield select();
  const body = buildSearchBody(state);
  yield put(oportunidadesRequest(body));
  yield put(obterFunilDeVendasRequest({ filters: body.filters }));
}

function* buscaTriggerWatcher() {
  yield takeLatest(
    [salvarFiltrosGrid.type, setPaginationModel.type, setSortModel.type],
    dispararBuscaOportunidades
  );
}

const mostrarOportunidadeApi = (values) => {
  return authenticatedRequest({
    url: '/oportunidade/show/' + values,
    method: 'GET'
  });
};

const mostrarOportunidade = basicFlow({
  actionGenerator: oportunidadeRequest,
  actionFailure: oportunidadeFailure,
  actionSuccess: oportunidadeSuccess,
  api: mostrarOportunidadeApi,
  postSuccess: function* ({ response }) {
    const ufs = yield select(dominioSelectors.getUfs);
    const tiposImovel = yield select(dominioSelectors.getTiposImovel);
    const tiposUtilizacao = yield select(dominioSelectors.getTiposUtilizacao);

    const tipoImovel = tiposImovel.find(
      (el) => el.id === response.data.tipoImovel
    );

    const tipoUtilizacaoImovel = tiposUtilizacao.find(
      (el) => el.id === response.data.tipoUtilizacaoImovel
    );

    const uf = ufs.find((el) => el.codigo === response.data.uf);

    const formattedResponse = {
      ...response.data,
      tipoImovel,
      tipoUtilizacaoImovel,
      uf
    };

    yield put(formatarOportunidade(formattedResponse));
  },

  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const mostrarOportunidadeImovelIq = basicFlow({
  actionGenerator: oportunidadeImovelIqRequest,
  actionFailure: oportunidadeImovelIqFailure,
  actionSuccess: oportunidadeImovelIqSuccess,
  api: mostrarOportunidadeApi,
  postSuccess: function* ({ response }) {
    const ufs = yield select(dominioSelectors.getUfs);
    const tiposImovel = yield select(dominioSelectors.getTiposImovel);
    const tiposUtilizacao = yield select(dominioSelectors.getTiposUtilizacao);

    const tipoImovel = tiposImovel.find(
      (el) => el.id === response.data.tipoImovel
    );

    const tipoUtilizacaoImovel = tiposUtilizacao.find(
      (el) => el.id === response.data.tipoUtilizacaoImovel
    );

    const uf = ufs.find((el) => el.codigo === response.data.uf);

    const formattedResponse = {
      ...response.data,
      tipoImovel,
      tipoUtilizacaoImovel,
      uf
    };

    yield put(formatarOportunidade(formattedResponse));
  },

  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const editarOportunidadeApi = ({ value }) => {
  const payload = {
    ...value,
    dataHoraAlteracao: new Date().toISOString(),
    cep: removeNonDigitsFromString(value.cep),
    telefoneContatoAvaliacao: removeNonDigitsFromString(
      value.telefoneContatoAvaliacao
    )
  };

  if (typeof payload.uf === 'object') {
    payload.uf = payload?.uf?.codigo;
  }

  if (typeof payload.tipoImovel === 'object') {
    payload.tipoImovel = payload?.tipoImovel?.id ?? null;
  }

  if (typeof payload.tipoUtilizacaoImovel === 'object') {
    payload.tipoUtilizacaoImovel = payload?.tipoUtilizacaoImovel?.id ?? null;
  }

  return authenticatedRequest({
    url: '/oportunidade/update/' + payload.idOportunidade,
    method: 'PUT',
    body: payload
  });
};

const editarOportunidade = basicFlow({
  actionGenerator: editarOportunidadeRequest,
  actionFailure: editarOportunidadeFailure,
  actionSuccess: editarOportunidadeSuccess,
  api: editarOportunidadeApi,
  postSuccess: function* ({ response, values }) {
    if (values.value.isImovel) {
      yield toast.success('Imóvel editado com sucesso!');
    } else if (values.value.isIq) {
      yield toast.success('Interveniente Quitante editado com sucesso!');
    } else {
      yield toast.success('Oportunidade editada com sucesso!');
    }
  },
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const editarOportunidadeImovelIq = basicFlow({
  actionGenerator: editarOportunidadeImovelIqRequest,
  actionFailure: editarOportunidadeImovelIqFailure,
  actionSuccess: editarOportunidadeImovelIqSuccess,
  api: editarOportunidadeApi,
  postSuccess: function* ({ response, values }) {
    if (values.value.isImovel) {
      yield toast.success('Imóvel editado com sucesso!');
    } else if (values.value.isIq) {
      yield toast.success('Interveniente Quitante editado com sucesso!');
    } else {
      yield toast.success('Oportunidade editada com sucesso!');
    }
    yield put(oportunidadeImovelIqRequest(values.value.idOportunidade));
  },
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

function* oportunidadesRouteWatcher() {
  yield routeWatcher(routes.OPORTUNIDADES, function* () {
    yield put(regionalRequest());
    yield put(parceiroRequest({ tipo: 'filtro' }));
    yield put(usuariosParceirosRequest({ tipo: 'filtro' }));
    yield put(usuariosRequest());
    yield put(etapasRequest());
    yield put(bancosRequest());
    yield put(operacaoRequest());
    yield* dispararBuscaOportunidades();
  });
}

function* oportunidadeRouteWatcher() {
  yield routeWatcher(routes.OPORTUNIDADE, function* () {
    const { id } = yield select(getPayload);
    yield put(operacaoRequest());
    yield put(etapasRequest());
    yield put(usuariosRequest());
    yield put(oportunidadeRequest(id));
    yield put(oportunidadeImovelIqRequest(id));
    yield put(actions.listarCompradoresRequest(id));
    yield put(actions.adicionarOportunidade(id));
    yield put(vendedoresActions.listarVendedoresRequest(id));
    yield put(vendedoresActions.adicionarOportunidade(id));
    yield put(atividadesActions.listarEtapasRequest(id));
    yield put(atividadesActions.setEtapa(0));
    yield put(atividadesActions.closeModal());
    yield put(atividadesActions.adicionarOportunidade(id));
    yield put(fupActions.listarFupsRequest(id));
    yield put(fupActions.adicionarOportunidade(id));
    yield put(documentosActions.listarEntidadesRelacionadasRequest(id));
    yield put(bancosRequest());
    yield put(documentosActions.adicionarOportunidade(id));
    yield put(alterarMenuSelecionado(0));
  });
}

function* mostrarImovelRouteWatcher() {
  yield routeWatcher(
    [routes.IMOVEL, routes.INTERVENIENTE_QUITANTE],
    function* () {
      const { id } = yield select(getPayload);
      yield put(oportunidadeImovelIqRequest(id));
    }
  );
}

const editarDataPrevistaApi = (values) => {
  values.dataPrevistaEmissaoContrato = formatters.dates.revert(
    values.dataPrevistaEmissaoContrato
  );
  return authenticatedRequest({
    url: '/oportunidade/update/data-prevista-contrato/' + values.idOportunidade,
    method: 'PUT',
    body: { ...values }
  });
};

const editarDataPrevista = basicFlow({
  actionGenerator: editarDataPrevistaRequest,
  actionFailure: editarDataPrevistaFailure,
  actionSuccess: editarDataPrevistaSuccess,
  api: editarDataPrevistaApi,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const editarTemperaturaApi = (values) => {
  return authenticatedRequest({
    url: '/oportunidade/update/temperatura/' + values.idOportunidade,
    method: 'PUT',
    body: { ...values }
  });
};

const editarTemperatura = basicFlow({
  actionGenerator: editarTemperaturaRequest,
  actionFailure: editarTemperaturaFailure,
  actionSuccess: editarTemperaturaSuccess,
  api: editarTemperaturaApi,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const editarSituacaoApi = (values) => {
  return authenticatedRequest({
    url: '/oportunidade/update/situacao/' + values.idOportunidade,
    method: 'PUT',
    body: { ...values }
  });
};

const editarSituacao = basicFlow({
  actionGenerator: editarSituacaoRequest,
  actionFailure: editarSituacaoFailure,
  actionSuccess: editarSituacaoSuccess,
  api: editarSituacaoApi,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

const obterEtapasApi = (values) => {
  return authenticatedRequest({
    url: '/oportunidade/obter-etapas/' + values,
    method: 'GET'
  });
};

const obterEtapas = basicFlow({
  actionGenerator: obterEtapasRequest,
  actionFailure: obterEtapasFailure,
  actionSuccess: obterEtapasSuccess,
  api: obterEtapasApi,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error: error });
  }
});

export const sagas = [
  listarOportunidades.watcher(),
  mostrarOportunidade.watcher(),
  mostrarOportunidadeImovelIq.watcher(),
  editarOportunidade.watcher(),
  buscarCep.watcher(),
  oportunidadesRouteWatcher(),
  oportunidadeRouteWatcher(),
  mostrarImovelRouteWatcher(),
  editarDataPrevista.watcher(),
  editarTemperatura.watcher(),
  editarSituacao.watcher(),
  obterEtapas.watcher(),
  editarOportunidadeImovelIq.watcher(),
  obterFunil.watcher(),
  buscaTriggerWatcher()
];
